import React from 'react';
import { useFormikContext } from 'formik';
import { object, string } from 'yup';
import { HeaderText } from '@el8/vital';

import { Box } from 'components';
import StateInputFormik from 'components/formik/StateInputFormik';
import TextInputFormik from 'components/formik/TextInputFormik';
import ZipCodeInputFormik from 'components/formik/ZipCodeInputFormik';
import { ZIP_CODE_REGEX } from 'utils/regex';

import StripeCardElement from './StripeCardElement';
import styles from './PaymentMethodForm.less';

export type PaymentMethodFormValues = {
  cardholder_name: string;
  email: string;
  address_line1: string;
  address_line2: string;
  city: string;
  state: string;
  zip: string;
};

export const paymentMethodValidationSchema = object().shape({
  cardholder_name: string().required('Required'),
  email: string()
    .required('Required')
    .email('Please enter a valid email')
    .max(255),
  address_line1: string()
    .required('Required')
    .max(256),
  address_line2: string().max(256),
  city: string()
    .required('Required')
    .max(128),
  state: string()
    .required('Required')
    .min(2)
    .max(2),
  zip: string()
    .required('Required')
    .matches(ZIP_CODE_REGEX, 'Please enter a valid zip code'),
});

export const paymentMethodInitialValues = {
  cardholder_name: '',
  email: '',
  address_line1: '',
  address_line2: '',
  city: '',
  state: '',
  zip: '',
};

const PaymentMethodForm: React.FC = () => {
  const { isSubmitting } = useFormikContext<PaymentMethodFormValues>();

  return (
    <>
      <HeaderText tag="h2" style={{ marginBottom: 4 }}>
        Billing info
      </HeaderText>
      <div className={styles.formGroup}>
        <TextInputFormik
          className={styles.block}
          name="cardholder_name"
          placeholder="Name on card"
        />
        <TextInputFormik className={styles.block} name="email" placeholder="Email" />
        <TextInputFormik className={styles.block} name="address_line1" placeholder="Address" />
        <TextInputFormik
          className={styles.block}
          name="address_line2"
          placeholder="Apartment/suite/other"
        />
        <Box>
          <TextInputFormik name="city" placeholder="City" style={{ flex: 1 }} />
          <StateInputFormik name="state" placeholder="State" style={{ margin: '0 8px' }} />
          <ZipCodeInputFormik name="zip" placeholder="Zip" />
        </Box>
      </div>
      <HeaderText tag="h2" style={{ marginBottom: 4 }}>
        Payment details
      </HeaderText>
      <div className={styles.formGroup}>
        <StripeCardElement disabled={isSubmitting} />
      </div>
    </>
  );
};

export default PaymentMethodForm;
